import React from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format } from "date-fns";
import { MetricDataPoint } from "@/types/monitoring";

interface MetricChartProps {
  data: MetricDataPoint[];
  color: string;
  unit: string;
  isLoading: boolean;
}

function computeYMax(dataMax: number, unit: string): number {
  const safeMax = Number.isFinite(dataMax) && dataMax > 0 ? dataMax : 1;
  const padded = safeMax + Math.max(safeMax * 0.2, 5);
  if (unit === "%") {
    return Math.min(100, Math.ceil(padded / 10) * 10);
  }
  return Math.ceil(padded);
}

export const MetricChart: React.FC<MetricChartProps> = ({
  data,
  color,
  unit,
  isLoading,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="h-[200px] w-full rounded-md bg-muted animate-pulse" />
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-[200px] w-full flex items-center justify-center text-sm text-muted-foreground">
        {t("monitoring.dashboard.noData")}
      </div>
    );
  }

  const chartData = data.map((d) => ({
    time: new Date(d.timestamp).getTime(),
    value: d.value,
  }));

  const dataMax = chartData.reduce(
    (max, point) => (point.value > max ? point.value : max),
    0,
  );
  const yMax = computeYMax(dataMax, unit);

  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="time"
          type="number"
          domain={["dataMin", "dataMax"]}
          tickFormatter={(ts) => format(new Date(ts), "HH:mm")}
          className="text-xs"
          tick={{ fontSize: 12 }}
        />
        <YAxis
          domain={[0, yMax]}
          allowDataOverflow={false}
          tickFormatter={(v) => `${v}${unit}`}
          className="text-xs"
          tick={{ fontSize: 12 }}
          width={50}
        />
        <Tooltip
          labelFormatter={(ts) =>
            format(new Date(ts as number), "MMM d, HH:mm:ss")
          }
          formatter={(value: unknown) => [
            `${Number(value).toFixed(1)}${unit}`,
            "",
          ]}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          fill={color}
          fillOpacity={0.15}
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
